<template>
  <b-row>
    <b-col xl="12">
      <transition name="slide">
        <b-card>
          <div slot="header">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <b-button
                  v-if="profile.role === 'root'"
                  variant="secondary"
                  size="sm"
                  class="mr-3"
                  @click="goBack"
                >
                  <i class="fas fa-arrow-left"></i> Voltar
                </b-button>
                <strong>Gestão de Comitês</strong>
              </div>
              <b-button
                v-if="profile.role === 'company-root'"
                variant="primary"
                @click="openCreateForm"
              >
                <i class="fas fa-plus-circle"></i> Criar Comitê
              </b-button>
            </div>
          </div>
          <b-card-body class="p-0">
            <div class="table-container">
              <b-table
                :items="committees"
                :fields="fields"
                :busy="loading"
                thead-class="committees-header"
                :tbody-tr-class="rowClass"
                borderless
                hover
                responsive
                show-empty
                empty-text="Nenhum comitê encontrado"
                @row-clicked="openEditForm"
              >
                <template #cell(name)="data">
                  <div class="committee-name">
                    <strong>{{ data.item.name }}</strong>
                  </div>
                </template>

                <template #cell(users)="data">
                  <div class="users-list">
                    <span
                      v-for="(user, index) in data.item.users"
                      :key="user._id"
                      class="user-name"
                    >
                      {{ user.name }} {{ user.surname
                      }}<span v-if="index < data.item.users.length - 1"
                        >,
                      </span>
                    </span>
                  </div>
                </template>

                <template #table-busy>
                  <div class="text-center py-5">
                    <b-spinner></b-spinner>
                    <span class="ml-2">Carregando...</span>
                  </div>
                </template>

                <template #empty>
                  <div class="text-muted text-center py-5">
                    <i class="fas fa-users fa-2x"></i>
                    <p class="mt-3">Nenhum comitê encontrado</p>
                    <p class="text-sm">
                      Clique em "Criar Comitê" para adicionar o primeiro comitê
                    </p>
                  </div>
                </template>
              </b-table>
            </div>
          </b-card-body>
        </b-card>
      </transition>
    </b-col>

    <!-- Sidebar para formulário -->
    <b-sidebar
      id="committee-form-sidebar"
      v-model="isFormVisible"
      :title="selectedCommittee ? 'Editar Comitê' : 'Novo Comitê'"
      width="50%"
      right
      shadow
      backdrop
    >
      <committee-form
        v-if="isFormVisible"
        :committee="selectedCommittee"
        :company-id="companyId"
        @submit="handleFormSubmit"
        @delete="handleDelete"
        @close="closeForm"
      />
    </b-sidebar>
  </b-row>
</template>

<script>
import { mapGetters } from "vuex";
import CommitteeForm from "./CommitteeForm.vue";
import {
  getCommittees,
  createCommittee,
  updateCommittee,
  deleteCommittee,
} from "@/api/committee";

export default {
  name: "CommitteeView",
  components: {
    CommitteeForm,
  },
  props: {
    id: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      committees: [],
      loading: false,
      isFormVisible: false,
      selectedCommittee: null,
      fields: [
        {
          key: "name",
          label: "COMITÊ",
          sortable: true,
          tdClass: "align-middle",
        },
        {
          key: "users",
          label: "USUÁRIOS",
          tdClass: "align-middle",
        },
      ],
    };
  },
  computed: {
    ...mapGetters(["profile"]),
    companyId() {
      return this.id || this.$route.params.id || this.profile.company;
    },
  },
  async created() {
    await this.loadCommittees();
  },
  watch: {
    "$route.params.id": {
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          this.loadCommittees();
        }
      },
      immediate: false,
    },
    companyId(newId, oldId) {
      if (newId && newId !== oldId) {
        this.loadCommittees();
      }
    },
  },
  methods: {
    goBack() {
      this.$router.push({ path: "/empresas" });
    },
    async loadCommittees() {
      this.loading = true;
      try {
        const response = await getCommittees(this.companyId);
        this.committees = response.data;
      } catch (error) {
        this.$bvToast.toast("Não foi possível carregar os comitês.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
        this.committees = [];
      } finally {
        this.loading = false;
      }
    },
    openCreateForm() {
      this.selectedCommittee = null;
      this.isFormVisible = true;
    },
    openEditForm(committee) {
      this.selectedCommittee = committee;
      this.isFormVisible = true;
    },
    closeForm() {
      this.isFormVisible = false;
      this.selectedCommittee = null;
    },

    async handleFormSubmit(formData) {
      try {
        const payload = {
          ...formData,
          company: this.companyId,
        };
        if (formData.id) {
          await updateCommittee(formData.id, payload);
        } else {
          await createCommittee(payload);
        }

        this.$bvToast.toast(
          `Comitê ${formData.id ? "atualizado" : "criado"} com sucesso.`,
          {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          }
        );

        this.closeForm();
        await this.loadCommittees();
      } catch (error) {
        this.$bvToast.toast(
          `Não foi possível ${formData.id ? "atualizar" : "criar"} o comitê.`,
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
      }
    },
    async handleDelete(committee) {
      try {
        await deleteCommittee(committee._id);

        this.$bvToast.toast("Comitê excluído com sucesso.", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });

        this.closeForm();
        await this.loadCommittees();
      } catch (error) {
        this.$bvToast.toast("Não foi possível excluir o comitê.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      }
    },

    rowClass(item, type) {
      if (!item || type !== "row") return "committee-row";
      return ["committee-row"];
    },
  },
};
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter,
.slide-leave-to {
  transform: translateX(-10px);
  opacity: 0;
}

.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.table-container >>> .committees-header th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
  font-size: 0.8rem;
  text-transform: uppercase;
  border: 0;
  white-space: nowrap;
}

.table-container >>> .committees-header th.sortable:hover {
  color: #0056b3;
}

.table-container >>> .committee-row {
  cursor: pointer;
  border-bottom: 1px solid #f1f3f4;
  transition: background-color 0.2s ease-in-out;
}

.table-container >>> .committee-row:hover {
  background-color: #e9ecef !important;
}

.table-container >>> tbody tr:last-of-type {
  border-bottom: none;
}

.table-container >>> .committee-row td {
  border-top: none;
  vertical-align: middle;
  padding: 0.85rem 0.75rem;
}

.committee-name {
  font-weight: 500;
  color: #495057;
}

.users-list {
  font-size: 0.9rem;
  color: #6c757d;
}

.user-name {
  display: inline;
}
</style>
